import { authFetch } from '@/services/apiClient'
import { DirectUploadUnavailable, uploadDirect } from '@/services/uploadClient'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5050/api'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
  code?: string   // รหัส error จาก backend เช่น PRE_ASSESSMENT_REQUIRED
}

export interface ApiJobCategory {
  job_category_id: number
  name: string
  slug: string
  icon_url: string | null
  sort_order: number
}

export interface ApiSkillTag {
  tag_id: number
  name: string
  category: string | null
}

export interface ApiJobCompany {
  company_id: number
  company_name: string
  logo_url: string | null
  industry: string | null
  company_size: string | null
  description: string | null
  website: string | null
  location: string | null
  is_verified: boolean
}

export interface ApiJobListItem {
  job_id: number
  job_number: string
  title: string
  company_id: number
  company_name: string
  company_logo_url: string | null   // โลโก้ที่บริษัทอัปโหลดในโปรไฟล์ (ไม่มี = โชว์ตัวอักษรย่อ)
  category_id: number
  work_type: string
  experience_level: string
  location: string
  is_remote: boolean
  salary_min: number | null
  salary_max: number | null
  currency: string
  status: string
  is_urgent: boolean
  is_featured: boolean
  view_count: number
  application_count: number
  published_at: string | null
  expires_at: string | null
  created_date: string
}

export interface ApiJobDetail extends ApiJobListItem {
  description: string
  requirements: string | null    // JSON string list — ต้อง JSON.parse เอง
  nice_to_have: string | null    // JSON string list
  screening_questions: ScreeningQuestion[] | null
  benefits: string[] | null
  tag_ids: number[]
  category: ApiJobCategory
  company: ApiJobCompany
  skills: string[]
}

// คำถามเพิ่มเติมของบริษัท — id ออกโดย server (q1, q2, ...)
export interface ScreeningQuestion {
  id?: string
  type: 'YES_NO' | 'CHOICE' | 'NUMBER' | 'TEXT'
  question: string
  required: boolean
  options?: string[]   // เฉพาะ CHOICE (2-6 ตัว)
}

export interface WorkHistoryEntry {
  position: string
  company: string
  start_date: string | null
  end_date: string | null
  current: boolean
  description: string
}

export interface EducationEntry {
  university: string
  faculty: string
  major: string
  gpa: number | string | null
  graduated: boolean
  graduation_date: string | null
}

export interface ScreeningAnswer {
  question_id: string
  question_text: string
  type: string
  answer: boolean | number | string
}

export type ApplicationDocumentKind = 'RESUME' | 'COVER_LETTER' | 'TRANSCRIPT' | 'CERTIFICATE'
export interface ApplicationDocument {
  id: string   // resume | cover_letter | transcript | certificate-<n>
  kind: ApplicationDocumentKind
  label: string
  ext: string
}

export interface ApiJobApplication {
  application_id: number
  job_id: number
  status: string
  cover_letter: string | null
  documents: ApplicationDocument[]   // ไฟล์แนบ (เก็บบน MinIO แบบส่วนตัว) — ดาวน์โหลดผ่าน downloadApplicationDocument
  experience_range: string | null
  expected_salary: number | null
  screening_answers: ScreeningAnswer[] | null
  work_history: WorkHistoryEntry[] | null
  education: EducationEntry[] | null
  skills: string[] | null
  applied_at: string
  applicant_name: string
  job_title: string
  job_status: string | null   // งานอาจถูกปิดรับ/ระงับไปแล้วหลังสมัคร
  company_name: string | null
  company_logo_url?: string | null
  location: string
  work_type: string
}

// ข้อมูลที่ส่งตอนยื่นใบสมัคร — ไม่มีไฟล์ (ไฟล์แนบอัปโหลดแยกด้วย uploadApplicationDocument หลังยื่นใบสมัครสำเร็จ)
export interface ApplyJobPayload {
  cover_letter?: string | null
  experience_range?: string | null
  expected_salary?: string | null
  screening_answers?: Record<string, boolean | string>
  work_history?: WorkHistoryEntry[]
  education?: EducationEntry[]
  skills?: string[]
}

export interface ApiJobSaved {
  saved_id: number
  job_id: number
  created_date: string
  job: ApiJobListItem
}

// ── Shared helpers — backend เก็บข้อมูลคนละรูปแบบจาก mock เดิม ต้องแปลงก่อนใช้เสมอ ──

// requirements/nice_to_have เก็บใน DB เป็น TEXT ที่บรรจุ JSON string list — ต้อง parse เอง
export function parseJsonList(text: string | null | undefined): string[] {
  if (!text) return []
  try {
    const arr = JSON.parse(text)
    return Array.isArray(arr) ? arr : []
  } catch {
    return []
  }
}

// backend ไม่มี field "โพสมาแล้วกี่วัน" ตรงๆ ต้องคำนวณเองจาก published_at
export function daysAgo(dateString: string | null | undefined): number {
  if (!dateString) return 0
  const diff = Date.now() - new Date(dateString).getTime()
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)))
}
export function postedText(dateString: string | null | undefined): string {
  const d = daysAgo(dateString)
  return d === 0 ? 'วันนี้' : d === 1 ? 'เมื่อวาน' : `${d} วันที่แล้ว`
}

// ใช้เป็นสีพื้นหลังของตัวอักษรย่อเมื่อบริษัทยังไม่ได้อัปโหลดโลโก้ (logo_url) — ดู components/ui/CompanyLogo.tsx
// ชื่อเดียวกันได้สีเดิมเสมอ ไม่สุ่มใหม่ทุกครั้งที่ render
const LOGO_COLORS = ['#6C63FF', '#0066cc', '#00c300', '#00b14f', '#e3001b', '#f59e0b', '#ec4899', '#06b6d4']
export function getColorFromName(name: string): string {
  let hash = 0
  for (const ch of name) hash = (hash + ch.charCodeAt(0)) % LOGO_COLORS.length
  return LOGO_COLORS[hash]
}

export const WORK_TYPE_LABELS: Record<string, string> = {
  FULL_TIME: 'งานประจำ', PART_TIME: 'พาร์ทไทม์', REMOTE: 'รีโมท',
  HYBRID: 'ไฮบริด', FREELANCE: 'ฟรีแลนซ์', INTERNSHIP: 'ฝึกงาน',
}
export const EXP_LABELS: Record<string, string> = {
  ENTRY: 'Entry Level', MID: 'Mid Level', SENIOR: 'Senior', LEAD: 'Lead / Manager', C_LEVEL: 'C-Level / Executive',
}
// ช่วงประสบการณ์ในใบสมัคร (enum ExperienceRange) — ลำดับตัวเลือกดึงจาก /api/enums, ที่นี่แค่ป้ายภาษาไทย
export const EXPERIENCE_RANGE_LABELS: Record<string, string> = {
  NONE: 'ไม่มีประสบการณ์', LT_1: 'น้อยกว่า 1 ปี', Y1: '1 ปี', Y2: '2 ปี', Y3: '3 ปี',
  Y4: '4 ปี', Y5: '5 ปี', Y6_10: '6-10 ปี', GT_10: 'มากกว่า 10 ปี',
}
// ชนิดคำถามเพิ่มเติมของบริษัท (enum ScreeningQuestionType)
export const SCREENING_TYPE_LABELS: Record<string, string> = {
  YES_NO: 'ใช่ / ไม่ใช่', CHOICE: 'เลือกคำตอบ', NUMBER: 'ตัวเลข', TEXT: 'ข้อความ',
}

async function parseJson<T>(res: Response): Promise<ApiResponse<T>> {
  try {
    return await res.json()
  } catch {
    return { success: false, error: 'เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ' }
  }
}

// ── Public (ไม่ต้อง login) ──────────────────────────────────────

// enum ทุกตัวในระบบ (ทุก domain) — scan มาจาก con_sqlalchemy.py ฝั่ง backend อัตโนมัติ
// ห้าม hardcode ค่า enum (work_type, experience_level ฯลฯ) ไว้ในฝั่ง frontend เด็ดขาด ให้ดึงจากตรงนี้เสมอ
export const getEnums = async (): Promise<Record<string, string[]>> => {
  const res = await fetch(`${API_URL}/enums`)
  const json = await parseJson<Record<string, string[]>>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'โหลด enums ไม่สำเร็จ')
  return json.data
}

export const getJobCategories = async (): Promise<ApiJobCategory[]> => {
  const res = await fetch(`${API_URL}/job-categories`)
  const json = await parseJson<ApiJobCategory[]>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'โหลดหมวดหมู่งานไม่สำเร็จ')
  return json.data
}

export const getSkillTags = async (): Promise<ApiSkillTag[]> => {
  const res = await fetch(`${API_URL}/skill-tags`)
  const json = await parseJson<ApiSkillTag[]>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'โหลดรายการทักษะไม่สำเร็จ')
  return json.data
}

export interface JobFilters {
  category_id?: number
  work_type?: string
  experience_level?: string
  location?: string
  is_remote?: boolean
  salary_min?: number
  search?: string
  sort?: 'newest' | 'salary-desc' | 'applicants'
}

export const getJobs = async (filters: JobFilters = {}): Promise<ApiJobListItem[]> => {
  const query = new URLSearchParams()
  if (filters.category_id) query.append('category_id', String(filters.category_id))
  if (filters.work_type) query.append('work_type', filters.work_type)
  if (filters.experience_level) query.append('experience_level', filters.experience_level)
  if (filters.location) query.append('location', filters.location)
  if (filters.is_remote) query.append('is_remote', 'true')
  if (filters.salary_min) query.append('salary_min', String(filters.salary_min))
  if (filters.search) query.append('search', filters.search)
  if (filters.sort) query.append('sort', filters.sort)
  const qs = query.toString()
  const res = await fetch(`${API_URL}/jobs${qs ? `?${qs}` : ''}`)
  const json = await parseJson<ApiJobListItem[]>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'โหลดประกาศงานไม่สำเร็จ')
  return json.data
}

export const getJobDetail = async (jobId: number | string): Promise<ApiJobDetail> => {
  const res = await fetch(`${API_URL}/jobs/${jobId}`)
  const json = await parseJson<ApiJobDetail>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'ไม่พบประกาศงานนี้')
  return json.data
}

// ── Company (ต้อง login, customer_type=COMPANY) ──────────────────

export interface CreateJobPayload {
  title: string
  category_id: number
  work_type: string
  experience_level: string
  location: string
  is_remote?: boolean
  salary_min?: number | null
  salary_max?: number | null
  currency?: string
  description: string
  requirements?: string | null   // JSON.stringify(string[]) ก่อนส่ง
  nice_to_have?: string | null   // JSON.stringify(string[]) ก่อนส่ง
  tag_ids?: number[]
  is_urgent?: boolean
  expires_at?: string | null
  screening_questions?: ScreeningQuestion[]
  benefits?: string[] | null
  status?: 'DRAFT' | 'ACTIVE'   // ACTIVE = เผยแพร่ทันที (บริษัทที่ยืนยันแล้ว), DRAFT = บันทึกเป็นร่าง
}

export const createJob = async (payload: CreateJobPayload): Promise<ApiJobDetail> => {
  const res = await authFetch('/jobs', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const json = await parseJson<ApiJobDetail>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'เผยแพร่ประกาศงานไม่สำเร็จ')
  return json.data
}

export const getMyJobs = async (): Promise<ApiJobListItem[]> => {
  const res = await authFetch('/my-jobs')
  const json = await parseJson<ApiJobListItem[]>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'โหลดประกาศงานของฉันไม่สำเร็จ')
  return json.data
}

// รายละเอียดเต็มของประกาศตัวเอง (ทุกสถานะ) — ใช้เติมฟอร์มแก้ไข
export const getMyJobDetail = async (jobId: number | string): Promise<ApiJobDetail> => {
  const res = await authFetch(`/my-jobs/${jobId}`)
  const json = await parseJson<ApiJobDetail>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'โหลดประกาศงานไม่สำเร็จ')
  return json.data
}

// แก้ไขประกาศ — มีผลทันที (ไม่ต้องอนุมัติ) ส่งเฉพาะ field ที่แก้ก็ได้
export const updateMyJob = async (jobId: number, payload: Partial<CreateJobPayload>): Promise<ApiJobDetail> => {
  const res = await authFetch(`/my-jobs/${jobId}`, { method: 'PUT', body: JSON.stringify(payload) })
  const json = await parseJson<ApiJobDetail>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'บันทึกการแก้ไขไม่สำเร็จ')
  return json.data
}

// เผยแพร่ร่าง (DRAFT→ACTIVE) / ปิดรับสมัคร (ACTIVE→CLOSED) / เปิดอีกครั้ง (CLOSED→ACTIVE)
export const setMyJobStatus = async (jobId: number, status: 'ACTIVE' | 'CLOSED'): Promise<ApiJobDetail> => {
  const res = await authFetch(`/my-jobs/${jobId}/status`, { method: 'PATCH', body: JSON.stringify({ status }) })
  const json = await parseJson<ApiJobDetail>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'เปลี่ยนสถานะประกาศไม่สำเร็จ')
  return json.data
}

// ทำสำเนาเป็นร่างใหม่ (ทักษะ + คำถามเพิ่มเติมติดไปด้วย)
export const duplicateMyJob = async (jobId: number): Promise<ApiJobDetail> => {
  const res = await authFetch(`/my-jobs/${jobId}/duplicate`, { method: 'POST' })
  const json = await parseJson<ApiJobDetail>(res)
  if (!json.success || !json.data) throw new Error(json.error || json.message || 'ทำสำเนาประกาศไม่สำเร็จ')
  return json.data
}

export const deleteMyJob = async (jobId: number): Promise<void> => {
  const res = await authFetch(`/my-jobs/${jobId}`, { method: 'DELETE' })
  const json = await parseJson<null>(res)
  if (!json.success) throw new Error(json.error || 'ลบประกาศงานไม่สำเร็จ')
}

// ── Job Application (ต้อง login, customer_type=INDIVIDUAL) ───────

// อัปโหลดไฟล์แนบของใบสมัคร (ผู้สมัครเจ้าของใบสมัครเท่านั้น) — RESUME/COVER_LETTER/TRANSCRIPT แนบใหม่ = แทนที่ · CERTIFICATE เพิ่มได้ ≤ 10 ไฟล์
// ส่งตรงไปที่เก็บไฟล์ด้วย Uppy (≤ 10 MB) แล้วให้ API ตรวจ/ผูกเข้าใบสมัคร · API ยังไม่มีอัปโหลดตรง → ถอยไปทางเดิม (request.files ≤ 5 MB)
export const uploadApplicationDocument = async (applicationId: number, kind: ApplicationDocumentKind, file: File): Promise<void> => {
  try {
    await uploadDirect(file, { purpose: 'APPLICATION_DOC', extra: { application_id: applicationId, kind } })
  } catch (e) {
    if (!(e instanceof DirectUploadUnavailable)) throw e
    await uploadApplicationDocumentLegacy(applicationId, kind, file)
  }
}

const uploadApplicationDocumentLegacy = async (applicationId: number, kind: ApplicationDocumentKind, file: File): Promise<void> => {
  const form = new FormData()
  form.append('kind', kind)
  form.append('file', file)
  const res = await authFetch(`/applications/${applicationId}/documents`, { method: 'POST', body: form })
  const json = await parseJson<{ documents: ApplicationDocument[] }>(res)
  if (!json.success) throw new Error(json.error || json.message || `แนบไฟล์ ${file.name} ไม่สำเร็จ`)
}

// ดาวน์โหลดไฟล์แนบ (ไฟล์เป็นส่วนตัว ต้องแนบ token จึงโหลดเป็น blob แล้วเปิด/บันทึกที่ฝั่ง browser)
export const downloadApplicationDocument = async (applicationId: number, doc: ApplicationDocument): Promise<void> => {
  const res = await authFetch(`/applications/${applicationId}/documents/${doc.id}`)
  if (!res.ok) {
    const json = await parseJson<unknown>(res)
    throw new Error(json.error || json.message || 'ดาวน์โหลดไฟล์ไม่สำเร็จ')
  }
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = `${doc.label}.${doc.ext}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export const applyJob = async (jobId: number | string, data: ApplyJobPayload): Promise<ApiJobApplication> => {
  const res = await authFetch(`/jobs/${jobId}/apply`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  const json = await parseJson<ApiJobApplication>(res)
  if (!json.success || !json.data) {
    // แนบรหัส error ไปด้วย ให้หน้าสมัครงานแยกกรณี "ยังไม่ได้ทำแบบประเมิน" (PRE_ASSESSMENT_REQUIRED) ได้
    throw Object.assign(new Error(json.error || json.message || 'ยื่นใบสมัครไม่สำเร็จ'), { code: json.code })
  }
  return json.data
}

export const getMyApplications = async (): Promise<ApiJobApplication[]> => {
  const res = await authFetch('/my-applications')
  const json = await parseJson<ApiJobApplication[]>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'โหลดใบสมัครของฉันไม่สำเร็จ')
  return json.data
}

// ── Job Applications (บริษัทดูผู้สมัครของงานตัวเอง) ────────────────
// ไม่ทำ: นัดสัมภาษณ์แบบมีวัน-เวลา และประวัติการติดต่อ (ตัดสินใจแล้วว่าไม่ทำ) — INTERVIEWED เป็นแค่สถานะ

// สกิลและประวัติอบรมของผู้สมัครจาก JOS CoWork (ผู้สมัครรับทราบตอนสมัครแล้ว) — API คำนวณให้ ไม่มีรหัสภายใน/อีเมล/เบอร์
export interface ApplicantLearning {
  skills: { skill_id: number; name: string; icon: string; category_name: string; done: number; total: number; full: boolean }[]
  trainings: {
    type: 'ONLINE' | 'ONSITE'
    title: string
    date: string | null
    end_date?: string | null
    batch_name?: string
    certificate_number?: string | null
  }[]
  summary: { skills: number; full_skills: number; trainings: number }
}

export interface ApiCompanyApplication extends ApiJobApplication {
  learning?: ApplicantLearning | null
  province: string | null
  phone: string | null
  email: string | null
  company_note: string | null
  company_saved: boolean
  viewed_at: string | null
  assessment_score: {
    score: number | null
    total: number
    section_scores: { code: string; title: string; score: number; total: number }[]
    time_spent_seconds: number | null
    time_limit_minutes: number | null
    submitted_at: string | null
  } | null
}

// สถานะที่บริษัทเปลี่ยนเองได้ (APPLIED เป็นค่าเริ่มต้น เปลี่ยนกลับไม่ได้)
export const COMPANY_SETTABLE_STATUSES = ['REVIEWED', 'INTERVIEWED', 'OFFERED', 'REJECTED'] as const
export type CompanySettableStatus = typeof COMPANY_SETTABLE_STATUSES[number]

export const APPLICATION_STATUS_LABELS: Record<string, string> = {
  APPLIED: 'ใหม่', REVIEWED: 'เปิดดูแล้ว', INTERVIEWED: 'นัดสัมภาษณ์',
  OFFERED: 'ผ่านการคัดเลือก', REJECTED: 'ไม่ผ่าน', WITHDRAWN: 'ถอนใบสมัคร',
}

export const getMyJobApplications = async (
  jobId: number | string, filters: { status?: string; saved?: boolean } = {},
): Promise<ApiCompanyApplication[]> => {
  const query = new URLSearchParams()
  if (filters.status) query.set('status', filters.status)
  if (filters.saved) query.set('saved', 'true')
  const qs = query.toString()
  const res = await authFetch(`/my-jobs/${jobId}/applications${qs ? `?${qs}` : ''}`)
  const json = await parseJson<ApiCompanyApplication[]>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'โหลดรายชื่อผู้สมัครไม่สำเร็จ')
  return json.data
}

// เปิดดูครั้งแรก (ยังเป็น APPLIED) — backend เปลี่ยนเป็น REVIEWED + บันทึก viewed_at ให้อัตโนมัติ
export const getMyJobApplicationDetail = async (
  jobId: number | string, applicationId: number,
): Promise<ApiCompanyApplication> => {
  const res = await authFetch(`/my-jobs/${jobId}/applications/${applicationId}`)
  const json = await parseJson<ApiCompanyApplication>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'โหลดรายละเอียดใบสมัครไม่สำเร็จ')
  return json.data
}

export const setJobApplicationStatus = async (
  jobId: number | string, applicationId: number, status: CompanySettableStatus,
): Promise<ApiCompanyApplication> => {
  const res = await authFetch(`/my-jobs/${jobId}/applications/${applicationId}/status`, {
    method: 'PATCH', body: JSON.stringify({ status }),
  })
  const json = await parseJson<ApiCompanyApplication>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'เปลี่ยนสถานะไม่สำเร็จ')
  return json.data
}

export const setJobApplicationSaved = async (
  jobId: number | string, applicationId: number, saved: boolean,
): Promise<ApiCompanyApplication> => {
  const res = await authFetch(`/my-jobs/${jobId}/applications/${applicationId}/saved`, {
    method: 'PATCH', body: JSON.stringify({ saved }),
  })
  const json = await parseJson<ApiCompanyApplication>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'บันทึกไม่สำเร็จ')
  return json.data
}

export const setJobApplicationNote = async (
  jobId: number | string, applicationId: number, note: string,
): Promise<ApiCompanyApplication> => {
  const res = await authFetch(`/my-jobs/${jobId}/applications/${applicationId}/note`, {
    method: 'PUT', body: JSON.stringify({ note }),
  })
  const json = await parseJson<ApiCompanyApplication>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'บันทึกโน้ตไม่สำเร็จ')
  return json.data
}

// ── Job Saved (ต้อง login) ────────────────────────────────────────

export const getMySavedJobs = async (): Promise<ApiJobSaved[]> => {
  const res = await authFetch('/job-saved')
  const json = await parseJson<ApiJobSaved[]>(res)
  if (!json.success || !json.data) throw new Error(json.error || 'โหลดงานที่บันทึกไว้ไม่สำเร็จ')
  return json.data
}

export const saveJob = async (jobId: number): Promise<void> => {
  const res = await authFetch('/job-saved', {
    method: 'POST',
    body: JSON.stringify({ job_id: jobId }),
  })
  const json = await parseJson<unknown>(res)
  if (!json.success) throw new Error(json.error || json.message || 'บันทึกงานไม่สำเร็จ')
}

export const unsaveJob = async (jobId: number): Promise<void> => {
  const res = await authFetch(`/job-saved/${jobId}`, { method: 'DELETE' })
  const json = await parseJson<unknown>(res)
  if (!json.success) throw new Error(json.error || json.message || 'เอาออกจากรายการที่บันทึกไม่สำเร็จ')
}
